import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Grid,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  History as HistoryIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Gavel as GavelIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import { purposeService, Purpose } from "../services/purpose-service";
import WithdrawalConfirmationDialog from "../components/WithdrawalConfirmationDialog";
import WithdrawalStatusChip from "../components/WithdrawalStatusChip";

interface PurposesPageProps {
  jwt: string;
  clientData: {
    subdomain: string;
    clientName: string;
  } | null;
}

type StatusFilter = "ALL" | "ACTIVE" | "WITHDRAWN" | "EXPIRED" | "LEGAL_RETENTION_ACTIVE";

const PurposesPage: React.FC<PurposesPageProps> = ({ jwt, clientData }) => {
  const navigate = useNavigate();

  // State management
  const [purposes, setPurposes] = useState<Purpose[]>([]);
  const [filteredPurposes, setFilteredPurposes] = useState<Purpose[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("ALL");

  // Withdrawal dialog state
  const [withdrawalDialog, setWithdrawalDialog] = useState<{
    open: boolean;
    purpose: Purpose | null;
    loading: boolean;
    error: string | null;
  }>({
    open: false,
    purpose: null,
    loading: false,
    error: null,
  });

  // Load purposes on component mount
  useEffect(() => {
    const loadPurposes = async () => {
      try {
        setLoading(true);
        setError(null);
        const purposesData = await purposeService.getPurposes(false); // Get all purposes
        setPurposes(purposesData);
        setFilteredPurposes(purposesData);
      } catch (err: any) {
        setError(err.message || "Failed to load purposes");
      } finally {
        setLoading(false);
      }
    };

    if (jwt) {
      loadPurposes();
    }
  }, [jwt]);

  // Filter purposes based on search and status
  useEffect(() => {
    let filtered = purposes;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (purpose) =>
          purpose.purposeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          purpose.purposeDescription.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== "ALL") {
      filtered = filtered.filter((purpose) => purpose.status === statusFilter);
    }

    setFilteredPurposes(filtered);
  }, [purposes, searchTerm, statusFilter]);

  const handlePurposeClick = (purposeId: number) => {
    navigate(`/purposes/${purposeId}`);
  };

  const handleWithdrawClick = (purposeId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    const purpose = purposes.find((p) => p.purposeId === purposeId);
    if (purpose) {
      setWithdrawalDialog({
        open: true,
        purpose,
        loading: false,
        error: null,
      });
    }
  };

  const handleWithdrawalConfirm = async (reason?: string) => {
    if (!withdrawalDialog.purpose) return;

    setWithdrawalDialog((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await purposeService.createWithdrawalRequest(withdrawalDialog.purpose.purposeId, reason);

      // Show success message
      setError(null);

      // Close dialog
      setWithdrawalDialog({
        open: false,
        purpose: null,
        loading: false,
        error: null,
      });

      // Reload purposes to show updated status
      const purposesData = await purposeService.getPurposes(false);
      setPurposes(purposesData);
      setFilteredPurposes(purposesData);

      // Show success message (you might want to use a snackbar instead)
      console.log("Withdrawal request created successfully:", response);
    } catch (err: any) {
      setWithdrawalDialog((prev) => ({
        ...prev,
        loading: false,
        error: err.message || "Failed to create withdrawal request",
      }));
    }
  };

  const handleWithdrawalClose = () => {
    setWithdrawalDialog({
      open: false,
      purpose: null,
      loading: false,
      error: null,
    });
  };

  const handleGrantClick = (purposeId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    // TODO: Implement grant functionality
    console.log("Grant clicked for purpose:", purposeId);
  };

  const handleViewWithdrawalDetails = (purposeId: number) => {
    const purpose = purposes.find((p) => p.purposeId === purposeId);
    if (purpose?.withdrawalRequest) {
      // TODO: Open a modal or navigate to withdrawal details page
      console.log("View withdrawal details for purpose:", purposeId, purpose.withdrawalRequest);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "success";
      case "WITHDRAWN":
        return "error";
      case "EXPIRED":
        return "warning";
      case "LEGAL_RETENTION_ACTIVE":
        return "info";
      case "LEGAL_RETENTION_EXPIRED":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return <CheckCircleIcon />;
      case "WITHDRAWN":
        return <BlockIcon />;
      case "EXPIRED":
        return <ScheduleIcon />;
      case "LEGAL_RETENTION_ACTIVE":
        return <GavelIcon />;
      default:
        return <ScheduleIcon />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading purposes...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header Section */}
      <Box mb={4}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            color: "var(--text-dark)",
            fontWeight: 700,
          }}
        >
          My Consent Purposes
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ color: "var(--text-medium)" }}>
          Manage your data processing consents and view their current status
        </Typography>
      </Box>

      {/* Filters and Search */}
      <Box mb={3} display="flex" gap={2} flexWrap="wrap" alignItems="center">
        <TextField
          placeholder="Search purposes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 300 }}
        />

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Status</InputLabel>
          <Select
            value={statusFilter}
            label="Filter by Status"
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            startAdornment={<FilterIcon sx={{ mr: 1 }} />}
          >
            <MenuItem value="ALL">All Purposes</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="WITHDRAWN">Withdrawn</MenuItem>
            <MenuItem value="EXPIRED">Expired</MenuItem>
            <MenuItem value="LEGAL_RETENTION_ACTIVE">Legal Retention</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Results Count */}
      <Box mb={2}>
        <Typography variant="body2" sx={{ color: "var(--text-medium)" }}>
          Showing {filteredPurposes.length} of {purposes.length} purposes
        </Typography>
      </Box>

      {/* Purposes Grid */}
      {filteredPurposes.length === 0 ? (
        <Box textAlign="center" py={8}>
          <Typography variant="h6" gutterBottom sx={{ color: "var(--text-medium)" }}>
            No purposes found
          </Typography>
          <Typography variant="body2" sx={{ color: "var(--text-light)" }}>
            {searchTerm || statusFilter !== "ALL" ? "Try adjusting your search or filter criteria" : "You don't have any consent purposes yet"}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredPurposes.map((purpose) => (
            <Grid item xs={12} md={6} lg={4} key={purpose.purposeId}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  cursor: "pointer",
                  backgroundColor: "var(--bg-primary)",
                  border: "1px solid var(--border-light)",
                  borderRadius: "var(--radius-md)",
                  "&:hover": {
                    boxShadow: "var(--shadow-medium)",
                    transform: "translateY(-2px)",
                    transition: "all 0.2s ease-in-out",
                    borderColor: "var(--primary-green)",
                  },
                }}
                onClick={() => handlePurposeClick(purpose.purposeId)}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Purpose Header */}
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography
                      variant="h6"
                      component="h3"
                      gutterBottom
                      sx={{
                        color: "var(--text-dark)",
                        fontWeight: 600,
                      }}
                    >
                      {purpose.purposeName}
                    </Typography>
                    <Chip
                      icon={getStatusIcon(purpose.status)}
                      label={purpose.status.replace("_", " ")}
                      color={getStatusColor(purpose.status) as any}
                      size="small"
                      sx={{
                        backgroundColor: purpose.status === "ACTIVE" ? "var(--bg-accent)" : undefined,
                        color: purpose.status === "ACTIVE" ? "var(--primary-green)" : undefined,
                        border: purpose.status === "ACTIVE" ? "1px solid var(--primary-green)" : undefined,
                      }}
                    />
                  </Box>

                  {/* Withdrawal Request Status */}
                  {purpose.withdrawalRequest && (
                    <Box mb={2}>
                      <WithdrawalStatusChip
                        withdrawalRequest={purpose.withdrawalRequest}
                        onViewDetails={() => handleViewWithdrawalDetails(purpose.purposeId)}
                      />
                    </Box>
                  )}

                  {/* Purpose Description */}
                  <Typography variant="body2" mb={2} sx={{ color: "var(--text-medium)" }}>
                    {purpose.purposeDescription}
                  </Typography>

                  {/* Lawful Ground */}
                  <Box mb={2}>
                    <Typography variant="caption" sx={{ color: "var(--text-light)" }}>
                      Lawful Ground:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "medium",
                        color: "var(--text-dark)",
                      }}
                    >
                      {purpose.lawfulGroundLabel}
                    </Typography>
                  </Box>

                  {/* Last Consent Date */}
                  <Box mb={2}>
                    <Typography variant="caption" sx={{ color: "var(--text-light)" }}>
                      Last Updated:
                    </Typography>
                    <Typography variant="body2" sx={{ color: "var(--text-dark)" }}>
                      {formatDate(purpose.lastConsentDate)}
                    </Typography>
                  </Box>

                  {/* Retention Info */}
                  {purpose.retentionExpirationDate && (
                    <Box mb={2}>
                      <Typography variant="caption" sx={{ color: "var(--text-light)" }}>
                        Retention Expires:
                      </Typography>
                      <Typography variant="body2" sx={{ color: "var(--text-dark)" }}>
                        {formatDate(purpose.retentionExpirationDate)}
                      </Typography>
                    </Box>
                  )}

                  {/* Legal Retention Warning */}
                  {purpose.legalRetentionRequired && (
                    <Alert
                      severity="info"
                      sx={{
                        mt: 2,
                        backgroundColor: "var(--bg-accent)",
                        border: "1px solid var(--primary-green)",
                        "& .MuiAlert-icon": {
                          color: "var(--primary-green)",
                        },
                        "& .MuiAlert-message": {
                          color: "var(--text-dark)",
                        },
                      }}
                    >
                      Legal retention period applies
                    </Alert>
                  )}
                </CardContent>

                <CardActions sx={{ justifyContent: "space-between", p: 2 }}>
                  <Tooltip title="View consent history">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePurposeClick(purpose.purposeId);
                      }}
                    >
                      <HistoryIcon />
                    </IconButton>
                  </Tooltip>

                  <Box>
                    {purpose.canModify && (
                      <>
                        {purpose.currentConsent ? (
                          <Tooltip
                            title={
                              purpose.withdrawalRequest
                                ? `Withdrawal request already pending (Request #${purpose.withdrawalRequest.requestId})`
                                : "Withdraw consent for this purpose"
                            }
                            arrow
                          >
                            <span>
                              <Button
                                size="small"
                                startIcon={<BlockIcon />}
                                onClick={(e) => handleWithdrawClick(purpose.purposeId, e)}
                                disabled={!!purpose.withdrawalRequest}
                                sx={{
                                  backgroundColor: purpose.withdrawalRequest ? "var(--bg-disabled)" : "var(--warning-red-light)",
                                  color: purpose.withdrawalRequest ? "var(--text-disabled)" : "var(--warning-text)",
                                  border: purpose.withdrawalRequest ? "1px solid var(--border-disabled)" : "1px solid var(--warning-red)",
                                  borderRadius: "var(--radius-sm)",
                                  fontWeight: 500,
                                  "&:hover": purpose.withdrawalRequest
                                    ? {}
                                    : {
                                        backgroundColor: "var(--warning-red)",
                                        borderColor: "var(--warning-red-dark)",
                                        color: "var(--text-white)",
                                        transform: "translateY(-1px)",
                                        boxShadow: "var(--shadow-light)",
                                      },
                                }}
                              >
                                Withdraw
                              </Button>
                            </span>
                          </Tooltip>
                        ) : (
                          <Button
                            size="small"
                            startIcon={<CheckCircleIcon />}
                            onClick={(e) => handleGrantClick(purpose.purposeId, e)}
                            sx={{
                              backgroundColor: "var(--primary-green)",
                              color: "var(--text-white)",
                              border: "none",
                              borderRadius: "var(--radius-sm)",
                              fontWeight: 600,
                              "&:hover": {
                                backgroundColor: "var(--accent-brown)",
                                transform: "translateY(-1px)",
                                boxShadow: "var(--shadow-light)",
                              },
                            }}
                          >
                            Grant
                          </Button>
                        )}
                      </>
                    )}
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Withdrawal Confirmation Dialog */}
      {withdrawalDialog.purpose && (
        <WithdrawalConfirmationDialog
          open={withdrawalDialog.open}
          onClose={handleWithdrawalClose}
          onConfirm={handleWithdrawalConfirm}
          purposeName={withdrawalDialog.purpose.purposeName}
          purposeDescription={withdrawalDialog.purpose.purposeDescription}
          loading={withdrawalDialog.loading}
          error={withdrawalDialog.error || undefined}
        />
      )}
    </Box>
  );
};

export default PurposesPage;
